"use client";

import React, { useState } from "react";
import { Shield, Cookie, Info, Settings, CircleCheck, ExternalLink } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
const cookieData = [{
  name: "session_id",
  provider: "Vår nettside",
  purpose: "Opprettholder brukersesjonen og sikrer at nettsiden fungerer som den skal under besøket.",
  expiry: "Økt (Slettes når nettleseren lukkes)",
  type: "Nødvendig"
}, {
  name: "cookie_consent",
  provider: "Vår nettside",
  purpose: "Lagrer dine preferanser for bruk av informasjonskapsler.",
  expiry: "1 år",
  type: "Nødvendig"
}, {
  name: "_ga",
  provider: "Google Analytics",
  purpose: "Brukes til å skille brukere for å samle inn statistikk om hvordan nettsiden brukes.",
  expiry: "2 år",
  type: "Analytisk"
}, {
  name: "_gid",
  provider: "Google Analytics",
  purpose: "Brukes til å skille brukere for statistiske formål.",
  expiry: "24 timer",
  type: "Analytisk"
}, {
  name: "_fbp",
  provider: "Meta (Facebook)",
  purpose: "Brukes til å levere målrettet annonsering og måle effekten av kampanjer.",
  expiry: "3 måneder",
  type: "Markedsføring"
}];
export default function Informasjonskapsler() {
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);
  const [marketingEnabled, setMarketingEnabled] = useState(false);
  return <section id="informasjonskapsler" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 max-w-4xl">
        
        {}
        <div className="mb-12 text-center md:text-left">
          <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-full mb-6">
            <Cookie className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold font-default text-foreground mb-4">
            Informasjonskapsler (Cookies)
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Sist oppdatert: 15. oktober 2023
          </p>
        </div>

        <div className="space-y-12">
          
          {}
          <section>
            <h2 className="text-2xl font-semibold font-default text-foreground mb-4 flex items-center gap-2">
              <Info className="w-6 h-6 text-primary" />
              Hva er informasjonskapsler?
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed font-sans">
              <p>
                Informasjonskapsler (cookies) er små tekstfiler som lagres på din datamaskin, nettbrett eller mobiltelefon når du besøker en nettside. De brukes for å få nettsider til å fungere mer effektivt, samt for å gi informasjon til eierne av nettsiden.
              </p>
              <p>
                Noen informasjonskapsler slettes når du lukker nettleseren din (økt-informasjonskapsler), mens andre forblir på enheten din i en forhåndsbestemt periode (vedvarende informasjonskapsler). Vi bruker både våre egne informasjonskapsler (førsteparts) og informasjonskapsler fra våre partnere (tredjeparts).
              </p>
            </div>
          </section>

          <Separator />

          {}
          <section>
            <h2 className="text-2xl font-semibold font-default text-foreground mb-6 flex items-center gap-2">
              <Shield className="w-6 h-6 text-primary" />
              Hvordan vi bruker informasjonskapsler
            </h2>
            <p className="text-muted-foreground mb-8 font-sans">
              Vi kategoriserer informasjonskapslene vi bruker i tre hovedgrupper. Du kan velge å akseptere eller avvise analytiske og markedsføringsrelaterte informasjonskapsler.
            </p>

            <div className="grid gap-6 md:grid-cols-3">
              <Card className="bg-card border-border shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-default flex items-center gap-2">
                    <CircleCheck className="w-5 h-5 text-green-500" />
                    Nødvendige
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground font-sans">
                  Disse er essensielle for at nettsiden skal fungere korrekt. De muliggjør grunnleggende funksjoner som sidenavigasjon og tilgang til sikre områder. Nettsiden kan ikke fungere optimalt uten disse.
                </CardContent>
              </Card>

              <Card className="bg-card border-border shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-default flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                    </div>
                    Analytiske
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground font-sans">
                  Hjelper oss å forstå hvordan besøkende samhandler med nettsiden ved å samle inn og rapportere informasjon anonymt. Dette lar oss forbedre brukeropplevelsen.
                </CardContent>
              </Card>

              <Card className="bg-card border-border shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-default flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-purple-500/20 flex items-center justify-center">
                      <div className="w-2.5 h-2.5 rounded-full bg-purple-500" />
                    </div>
                    Markedsføring
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground font-sans">
                  Brukes til å spore besøkende på tvers av nettsider. Hensikten er å vise annonser som er relevante og engasjerende for den enkelte bruker, og dermed mer verdifulle for utgivere og tredjeparts annonsører.
                </CardContent>
              </Card>
            </div>
          </section>

          <Separator />

          {}
          <section>
            <h2 className="text-2xl font-semibold font-default text-foreground mb-6">
              Detaljert oversikt
            </h2>
            <p className="text-muted-foreground mb-6 font-sans">
              Nedenfor finner du en detaljert liste over de spesifikke informasjonskapslene vi bruker på vår plattform.
            </p>
            
            <div className="border border-border rounded-xl overflow-hidden bg-card">
              <Table>
                <TableHeader className="bg-muted/50">
                  <TableRow>
                    <TableHead className="w-[200px]">Navn & Leverandør</TableHead>
                    <TableHead>Formål</TableHead>
                    <TableHead className="w-[150px]">Varighet</TableHead>
                    <TableHead className="w-[120px]">Type</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cookieData.map((cookie, i) => <TableRow key={cookie.name} data-index={i}>
                      <TableCell className="font-medium">
                        <div className="flex flex-col">
                          <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded w-fit mb-1">{cookie.name}</span>
                          <span className="text-xs text-muted-foreground">{cookie.provider}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{cookie.purpose}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{cookie.expiry}</TableCell>
                      <TableCell>
                        <Badge variant={cookie.type === "Nødvendig" ? "default" : cookie.type === "Analytisk" ? "secondary" : "outline"} className="font-normal">
                          {cookie.type}
                        </Badge>
                      </TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </div>
          </section>

          <Separator />

          {}
          <section>
            <h2 className="text-2xl font-semibold font-default text-foreground mb-6 flex items-center gap-2">
              <Settings className="w-6 h-6 text-primary" />
              Administrer dine preferanser
            </h2>
            <p className="text-muted-foreground mb-6 font-sans">
              Du kan når som helst endre dine preferanser for informasjonskapsler. Vær oppmerksom på at blokkering av visse typer informasjonskapsler kan påvirke din opplevelse av nettsiden og tjenestene vi kan tilby.
            </p>

            <Card className="bg-card border-border shadow-md max-w-2xl">
              <CardHeader>
                <CardTitle className="font-default">Dine nåværende innstillinger</CardTitle>
                <CardDescription>Bruk bryterne nedenfor for å tilpasse hvilke informasjonskapsler du tillater.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between space-x-4">
                  <div className="flex flex-col space-y-1">
                    <Label className="text-base font-semibold">Nødvendige informasjonskapsler</Label>
                    <span className="text-sm text-muted-foreground">Alltid aktiv. Kan ikke slås av da nettsiden krever disse for å fungere.</span>
                  </div>
                  <Switch checked={true} disabled />
                </div>
                <Separator />
                <div className="flex items-center justify-between space-x-4">
                  <div className="flex flex-col space-y-1">
                    <Label htmlFor="analytics-toggle" className="text-base font-semibold cursor-pointer">Analytiske informasjonskapsler</Label>
                    <span className="text-sm text-muted-foreground">Hjelper oss å forbedre nettsiden ved å samle inn anonym bruksdata.</span>
                  </div>
                  <Switch id="analytics-toggle" checked={analyticsEnabled} onCheckedChange={setAnalyticsEnabled} />
                </div>
                <Separator />
                <div className="flex items-center justify-between space-x-4">
                  <div className="flex flex-col space-y-1">
                    <Label htmlFor="marketing-toggle" className="text-base font-semibold cursor-pointer">Markedsføringskapsler</Label>
                    <span className="text-sm text-muted-foreground">Tillater oss og våre partnere å vise deg relevante annonser.</span>
                  </div>
                  <Switch id="marketing-toggle" checked={marketingEnabled} onCheckedChange={setMarketingEnabled} />
                </div>
              </CardContent>
              <CardFooter className="bg-muted/30 border-t border-border pt-6">
                <Button className="w-full sm:w-auto">Lagre preferanser</Button>
              </CardFooter>
            </Card>

            <div className="mt-8 p-4 bg-secondary/50 rounded-lg border border-border">
              <h3 className="font-semibold font-default text-foreground mb-2">Nettleserinnstillinger</h3>
              <p className="text-sm text-muted-foreground font-sans mb-4">
                I tillegg til innstillingene ovenfor, kan du også kontrollere og slette informasjonskapsler gjennom innstillingene i nettleseren din. Hvordan du gjør dette avhenger av hvilken nettleser du bruker.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="https://support.google.com/chrome/answer/95647" newTab className="text-sm text-primary hover:underline inline-flex items-center gap-1">
                  Google Chrome <ExternalLink className="w-3 h-3" />
                </Link>
                <Link to="https://support.apple.com/no-no/guide/safari/sfri11471/mac" newTab className="text-sm text-primary hover:underline inline-flex items-center gap-1">
                  Apple Safari <ExternalLink className="w-3 h-3" />
                </Link>
                <Link to="https://support.mozilla.org/no/kb/slett-informasjonskapsler-fjerne-informasjon-nettsider" newTab className="text-sm text-primary hover:underline inline-flex items-center gap-1">
                  Mozilla Firefox <ExternalLink className="w-3 h-3" />
                </Link>
                <Link to="https://support.microsoft.com/nb-no/microsoft-edge/slette-informasjonskapsler-i-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" newTab className="text-sm text-primary hover:underline inline-flex items-center gap-1">
                  Microsoft Edge <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
            </div>
          </section>

          <Separator />

          {}
          <section>
            <h2 className="text-2xl font-semibold font-default text-foreground mb-4">
              Personvern og dine rettigheter (GDPR)
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed font-sans">
              <p>
                Når informasjonskapsler samler inn personopplysninger (som for eksempel IP-adresser eller unike identifikatorer), behandler vi disse i samsvar med personvernforordningen (GDPR). Vårt behandlingsgrunnlag for nødvendige informasjonskapsler er vår berettigede interesse i å levere en fungerende tjeneste. For analytiske og markedsføringsrelaterte informasjonskapsler er behandlingsgrunnlaget ditt samtykke.
              </p>
              <p>
                Du har rett til å be om innsyn, retting, sletting eller begrensning av behandlingen av dine personopplysninger. For mer detaljert informasjon om hvordan vi behandler personopplysninger og dine rettigheter, vennligst les vår fullstendige personvernerklæring.
              </p>
              <div className="pt-2">
                <Button variant="outline" asChild>
                  <Link to="/PrivacyPolicyPage">Les vår personvernerklæring</Link>
                </Button>
              </div>
            </div>
          </section>

        </div>
      </div>
    </section>;
}